import { Customer, Supplier, User, getDisplayOrderNumber } from '../types/order';
import { asArray, asBoolean, asOptionalString, asRecord, asString } from './common';
import { normalizeCustomer, normalizeUser } from './order';
import { buildCustomerSearchText } from '../modules/reference-data/customerSearch';
import { buildSupplierSearchText } from '../modules/reference-data/supplierSearch';

export interface CustomerOptionDto extends Customer {
  label: string;
  searchText: string;
}

export interface UserOptionDto extends User {
  label: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SupplierOptionDto extends Supplier {
  label: string;
  searchText: string;
}

export interface FactoryOptionDto {
  id: string;
  name: string;
  address?: string | null;
  taxId?: string | null;
  label: string;
}

export function normalizeCustomerOption(raw: unknown, entity = 'customerOption'): CustomerOptionDto {
  const customer = normalizeCustomer(raw, entity) ?? {
    id: '',
    name: '',
    createdAt: undefined,
    updatedAt: undefined,
  };

  return {
    ...customer,
    label: customer.name,
    searchText: buildCustomerSearchText(customer),
  };
}

export function normalizeCustomerOptions(raw: unknown, entity = 'customerOptions'): CustomerOptionDto[] {
  return asArray(raw, (item, index) => normalizeCustomerOption(item, `${entity}[${index}]`));
}

export function normalizeUserOption(raw: unknown, entity = 'userOption'): UserOptionDto {
  const record = asRecord(raw);
  const user = normalizeUser(raw, entity) ?? {
    id: '',
    email: '',
    name: '',
    role: 'OPERATIONS',
  };

  return {
    ...user,
    label: [user.name, user.email].filter(Boolean).join(' '),
    createdAt: asOptionalString(record?.createdAt),
    updatedAt: asOptionalString(record?.updatedAt),
  };
}

export function normalizeUserOptions(raw: unknown, entity = 'userOptions'): UserOptionDto[] {
  return asArray(raw, (item, index) => normalizeUserOption(item, `${entity}[${index}]`));
}

export function normalizeSupplierOption(raw: unknown, entity = 'supplierOption'): SupplierOptionDto {
  const record = asRecord(raw);
  const contactPeople = asArray(record?.contactPeople, (item) => {
    const contact = asRecord(item);

    return {
      name: asOptionalString(contact?.name),
      phone: asOptionalString(contact?.phone),
      email: asOptionalString(contact?.email)
    };
  });

  const supplier = {
    id: asString(record?.id),
    name: asString(record?.name),
    contactPerson: asOptionalString(record?.contactPerson),
    contactPeople,
    address: asOptionalString(record?.address),
    taxId: asOptionalString(record?.taxId),
    email: asOptionalString(record?.email),
    phone: asOptionalString(record?.phone),
    defaultCurrency: asOptionalString(record?.defaultCurrency),
    createdAt: asString(record?.createdAt),
    updatedAt: asString(record?.updatedAt),
  };

  return {
    ...supplier,
    label: supplier.name,
    searchText: buildSupplierSearchText(supplier),
  };
}

export function normalizeSupplierOptions(raw: unknown, entity = 'supplierOptions'): SupplierOptionDto[] {
  return asArray(raw, (item, index) => normalizeSupplierOption(item, `${entity}[${index}]`));
}

export function normalizeFactoryOption(raw: unknown): FactoryOptionDto {
  const record = asRecord(raw);
  const name = asString(record?.name);

  return {
    id: asString(record?.id),
    name,
    address: asOptionalString(record?.address),
    taxId: asOptionalString(record?.taxId),
    label: name,
  };
}

export function normalizeFactoryOptions(raw: unknown): FactoryOptionDto[] {
  return asArray(raw, (item) => normalizeFactoryOption(item));
}

export interface TaskOrderOptionDto {
  id: string;
  orderNumber: string;
  displayOrderNumber: string;
  customerName: string;
  label: string;
  searchText: string;
}

export function normalizeTaskOrderOption(raw: unknown, entity = 'taskOrderOption'): TaskOrderOptionDto {
  const record = asRecord(raw);
  const customer = asRecord(record?.customer);
  const customerName = asOptionalString(record?.customerName) || asOptionalString(customer?.name) || '';
  const orderNumber = asString(record?.orderNumber);
  const manualOrderNumber = asOptionalString(record?.manualOrderNumber);
  const displayOrderNumber = getDisplayOrderNumber({
    orderNumber,
    manualOrderNumber,
    useManualNumbering: asBoolean(record?.useManualNumbering),
  });

  return {
    id: asString(record?.id),
    orderNumber,
    displayOrderNumber,
    customerName,
    label: [displayOrderNumber, customerName].filter(Boolean).join(' - '),
    searchText: [
      displayOrderNumber,
      orderNumber,
      manualOrderNumber,
      customerName,
    ].filter(Boolean).join(' ').toLowerCase(),
  };
}

export function normalizeTaskOrderOptions(raw: unknown, entity = 'taskOrderOptions'): TaskOrderOptionDto[] {
  return asArray(raw, (item, index) => normalizeTaskOrderOption(item, `${entity}[${index}]`));
}
